<script setup>
import { computed } from 'vue';

const props = defineProps({
  addressLine1: {
    type: String,
    default: ''
  },
  addressLine2: {
    type: String,
    default: ''
  },
  city: {
    type: String,
    default: ''
  },
  province: {
    type: String,
    default: ''
  },
  postalCode: {
    type: String,
    default: ''
  },
  country: {
    type: String,
    default: 'CA'
  },
  addressLine1Error: {
    type: String,
    default: ''
  },
  postalCodeError: {
    type: String,
    default: ''
  },
  availableProvinces: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits([
  'update:addressLine1',
  'update:addressLine2',
  'update:city',
  'update:province',
  'update:postalCode',
  'update:country',
  'validate-address-line1',
  'validate-postal-code'
]);

// Postal code validation function
function validatePostalCode(value) {
  if (!value) return true;
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (cleaned.length === 3) {
    const threeCharRegex = /^[A-Z]\d[A-Z]$/;
    return threeCharRegex.test(cleaned);
  }
  if (cleaned.length === 6) {
    const sixCharRegex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
    return sixCharRegex.test(cleaned);
  }
  return false;
}

// Format postal code
function formatPostalCode(value) {
  if (!value) return '';
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  const sanitized = cleaned.replace(/[^A-Z0-9]/g, '');
  const limited = sanitized.slice(0, 6);
  if (limited.length === 6) {
    return `${limited.slice(0, 3)} ${limited.slice(3)}`;
  }
  return limited;
}

function handlePostalCodeInput(value) {
  const formatted = formatPostalCode(value);
  emit('update:postalCode', formatted);
  emit('validate-postal-code');
}

function handleCountryChange(value) {
  emit('update:country', value);
  emit('update:province', '');
  emit('update:postalCode', '');
  emit('country-changed', value);
}
</script>

<template>
  <div class="space-y-3 mt-4 pl-8">
    <!-- Address Line 1 (Mandatory) -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Address Line 1 <span class="text-red-500">*</span>
      </label>
      <input 
        :value="addressLine1" 
        @input="emit('update:addressLine1', $event.target.value)"
        @blur="emit('validate-address-line1')"
        type="text" 
        placeholder="Address Line 1"
        class="w-full px-4 py-2 border rounded-lg focus:border-transparent"
        :class="addressLine1Error ? 'border-red-500' : 'border-gray-300'" />
      <p v-if="addressLine1Error" class="text-xs text-red-500 mt-1">{{ addressLine1Error }}</p>
    </div>
    
    <!-- Address Line 2 (Optional) -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (Optional)</label>
      <input 
        :value="addressLine2" 
        @input="emit('update:addressLine2', $event.target.value)"
        type="text" 
        placeholder="Address Line 2"
        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent" />
    </div>
    
    <div class="grid grid-cols-2 gap-3">
      <!-- City -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
        <input 
          :value="city" 
          @input="emit('update:city', $event.target.value)"
          type="text" 
          placeholder="City"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent" />
      </div>
      
      <!-- Province/State (Dropdown when country is selected) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Province/State</label>
        <select 
          :value="province"
          @change="emit('update:province', $event.target.value)"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
          :disabled="!country || (country !== 'CA' && country !== 'US')">
          <option value="">Select Province/State</option>
          <option v-for="prov in availableProvinces" :key="prov.value" :value="prov.value">
            {{ prov.label }}
          </option>
        </select>
      </div>
    </div>
    
    <div class="grid grid-cols-2 gap-3">
      <!-- Postal Code -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
        <input 
          :value="postalCode" 
          @input="handlePostalCodeInput($event.target.value)"
          @blur="emit('validate-postal-code')"
          type="text" 
          placeholder="K6H or H3A 2S9"
          class="w-full px-4 py-2 border rounded-lg focus:border-transparent"
          :class="postalCodeError ? 'border-red-500' : 'border-gray-300'"
          maxlength="7" />
        <p v-if="postalCodeError" class="text-xs text-red-500 mt-1">{{ postalCodeError }}</p>
        <p v-if="!postalCodeError && postalCode" class="text-xs text-gray-500 mt-1">Format: K6H or H3A 2S9</p>
      </div>
      
      <!-- Country (Dropdown: US or CA) -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Country <span class="text-red-500">*</span></label>
        <select 
          :value="country"
          @change="handleCountryChange($event.target.value)"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent">
          <option value="CA">Canada</option>
          <option value="US">United States</option>
        </select>
      </div>
    </div>
  </div>
</template>

<style scoped>
input.toggle:checked {
  background-color: #a855f7 ;
}
</style>